import { googleMapsApiKey } from '../../config';

export interface Location {
    suburb: string;
    postcode?: string;
    /** short code */
    state?: string;
    /** short code, eg 'AU' */
    country?: string;
}

export interface SimpleGeocodeResult {
    location: Location;
    formatted_address: string;
    bounds: {
        north: number;
        south: number;
        east: number;
        west: number;
    };
}

export interface GeocoderResult {
    types: string[];
    formatted_address: string;
    address_components: Array<{
        short_name: string;
        long_name: string;
        postcode_localities: string[];
        types: string[];    // street_number, street_address, route, locality, political, administrative_area_level_1/2, country, postal_code
    }>;
    partial_match: boolean;
    place_id: string;
    postcode_localities: string[];
    geometry: {
        location: LatLng;
        location_type: string;  // 'ROOFTOP
        viewport: LatLngBounds;
        bounds: LatLngBounds;
    };
}

interface LatLng {
    lat: number;
    lng: number;
}

interface LatLngBounds {
    northeast: LatLng;
    southwest: LatLng;
}

function geocodeFromCache(address: string): SimpleGeocodeResult[] {
    const result = localStorage.getItem('geocode:' + address);
    return result && JSON.parse(result).results;
}

function cacheGeocodeResults(address: string, results: SimpleGeocodeResult[], ttl: number) {
    localStorage.setItem('geocode:' + address, JSON.stringify({results, expiry: Date.now() + ttl}));
}

// TODO: also consider https://www.mapbox.com/pricing/ For now, Google give $200/month free usage
export class GoogleGeocoder {
    constructor() {
        const now = Date.now();
        for (const key in localStorage) {
            if (key.match(/^geocode:/)) {
                const item = JSON.parse(localStorage[key]);
                if (item.expiry < now) {
                    localStorage.removeItem(key);
                }
            }
        }
    }

    geocode(address: string, country?: string, ttl: number = 604800000): Promise<SimpleGeocodeResult[]> {
        let key = address;
        if (country) {
            key += '/' + country;
        }

        const cached = geocodeFromCache(key);
        if (cached) {
            return Promise.resolve(cached);
            // return new Promise(resolve => {
            //     setTimeout(() => resolve(cached), 10);
            // });
        }

        let url = 'https://maps.googleapis.com/maps/api/geocode/json?key=' + googleMapsApiKey + '&address=' + address;
        if (country) {
            // country = country.toLowerCase();
            url += '&region=' + ({gb: 'uk'}[country] || country);
        }

        return fetch(url)
            .then(response => response.json())
            .then(response => {
                // console.info(response);
                const {results, status} = response as any;

                if (status === 'ZERO_RESULTS') {
                    return [];
                }
                if (status !== 'OK') {
                    throw status;
                }

                const simpleResults = results.map((result: GeocoderResult) => {
                    const bounds = result.geometry.bounds || result.geometry.viewport;

                    return {
                        location: result.address_components.reduce((location: Location, component) => {
                            if (component.types.indexOf('locality') >= 0) {
                                location.suburb = component.long_name;
                            } else if (!location.suburb && component.types.indexOf('colloquial_area') >= 0) {
                                location.suburb = component.long_name;
                            } else if (component.types.indexOf('administrative_area_level_1') >= 0) {
                                location.state = component.short_name;
                            } else if (component.types.indexOf('postal_code') >= 0) {
                                location.postcode = component.short_name;
                            } else if (component.types.indexOf('country') >= 0) {
                                location.country = component.short_name.toLowerCase();
                            }
                            return location;
                        }, {}),
                        formatted_address: result.formatted_address,
                        bounds: {
                            north: bounds.northeast.lat,
                            south: bounds.southwest.lat,
                            east: bounds.northeast.lng,
                            west: bounds.southwest.lng
                        }
                    } as SimpleGeocodeResult;
                });

                cacheGeocodeResults(key, simpleResults, ttl);
                return simpleResults;
            });
    }
}

/*
export class GoogleMapsGeocoder {
    private geocoder: google.maps.Geocoder;

    constructor(/!*google: GoogleAPI*!/) {
        this.geocoder = new google.maps.Geocoder();
    }

    geocode(address: string, callback: (results: GeocoderResult[], status: string /!* GeocoderStatus*!/) => void) {
        const results = geocodeFromCache(address);
        if (results) {
            return callback(results, 'OK');
        }

        this.geocoder.geocode({address}, (results, status) => {
            if (status as unknown === 'OK') {
                cacheGeocodeResults(address, results);
            }
            callback(results as GeocoderResult[], (status as unknown) as string);
        });
    }
}
*/
